import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, GraduationCap } from 'lucide-react';
import { useUser } from '@/context/UserContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MemeademicAdvisor = () => {
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [showGraduation, setShowGraduation] = useState(false);
  const [hasShownGraduation, setHasShownGraduation] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "GM! I'm your Memeademic Advisor.\n\nAsk me about:\n- How to use the platform\n- Course requirements\n- Quiz tips\n\nWhat do you need, retard?"
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  // Auto-open chatbot on first scroll
  useEffect(() => {
    if (hasAutoOpened) return;

    const handleFirstScroll = () => {
      if (!hasAutoOpened) {
        setIsOpen(true);
        setHasAutoOpened(true);
        window.removeEventListener('scroll', handleFirstScroll);
      }
    };

    window.addEventListener('scroll', handleFirstScroll);
    return () => window.removeEventListener('scroll', handleFirstScroll);
  }, [hasAutoOpened]);

  // Check if user has graduated (all 4 courses passed)
  useEffect(() => {
    if (user?.progress && !hasShownGraduation) {
      const allPassed = ['memeglish', 'memethematics', 'memestory', 'memeart'].every(
        course => user.progress[course]?.quiz_passed
      );
      if (allPassed) {
        // Show graduation popup after a short delay
        setTimeout(() => {
          setShowGraduation(true);
          setHasShownGraduation(true);
        }, 1000);
        
        // Auto-hide after 8 seconds
        setTimeout(() => {
          setShowGraduation(false);
        }, 9000);
      }
    }
  }, [user?.progress, hasShownGraduation]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        user_id: user?.id || 'guest',
        message: userMessage,
        session_id: sessionId
      });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Something went wrong.\n\nTry again, retard."
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Graduation Popup */}
      <AnimatePresence>
        {showGraduation && (
          <motion.div
            initial={{ opacity: 0, x: 100, y: 20 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 100 }}
            className="fixed bottom-36 right-6 z-[70] w-[320px] p-4 rounded-2xl bg-pump-green/20 border border-pump-green/50 shadow-[0_0_30px_rgba(57,255,20,0.3)]"
            data-testid="graduation-popup"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-pump-green flex items-center justify-center shrink-0">
                <GraduationCap className="text-black" size={20} />
              </div>
              <div>
                <p className="text-pump-green font-bold text-sm mb-1">Memeademic Advisor</p>
                <p className="text-white text-sm leading-relaxed">
                  Congratulations you're no longer a retard, now you're a trader :)
                </p>
              </div>
              <button
                onClick={() => setShowGraduation(false)}
                className="text-zinc-400 hover:text-white p-1"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAB Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-20 right-6 z-[60] w-14 h-14 rounded-full bg-degen-purple flex items-center justify-center shadow-lg hover:shadow-[0_0_30px_rgba(157,0,255,0.5)] transition-all ${isOpen ? 'scale-0' : 'scale-100'}`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="advisor-fab"
      >
        <MessageCircle className="text-white" size={24} />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed bottom-20 right-6 z-[60] w-[380px] h-[520px] max-w-[calc(100vw-48px)] max-h-[calc(100vh-120px)] flex flex-col rounded-2xl overflow-hidden border border-degen-purple/50 shadow-[0_0_40px_rgba(157,0,255,0.3)]"
            data-testid="advisor-chat-window"
          >
            {/* Terminal Header */}
            <div className="bg-pump-surface border-b border-white/10 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-degen-purple/20 flex items-center justify-center">
                  <Bot className="text-degen-purple" size={20} />
                </div>
                <div>
                  <h3 className="font-unbounded font-bold text-white text-sm">Memeademic Advisor</h3>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-pump-green animate-pulse"></span>
                    <span className="text-xs text-zinc-500 font-mono">Online</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-white transition-colors"
                data-testid="close-advisor-btn"
              >
                <X size={20} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-pump-black font-mono text-sm">
              {messages.map((msg, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] p-3 rounded-lg whitespace-pre-line ${
                      msg.role === 'user'
                        ? 'bg-degen-purple/20 text-white border border-degen-purple/30'
                        : 'bg-pump-surface text-zinc-300 border border-white/10'
                    }`}
                  >
                    {msg.role === 'assistant' && (
                      <span className="text-pump-green">{'> '}</span>
                    )}
                    {msg.content}
                  </div>
                </motion.div>
              ))}
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-pump-surface text-zinc-300 p-3 rounded-lg border border-white/10">
                    <span className="text-pump-green">{'> '}</span>
                    <span className="animate-pulse">Thinking...</span>
                    <span className="animate-blink">|</span>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="bg-pump-surface border-t border-white/10 p-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask me anything..."
                  className="flex-1 bg-pump-card border border-white/10 rounded-lg px-4 py-2 text-white placeholder:text-zinc-600 focus:outline-none focus:border-degen-purple/50 font-mono text-sm"
                  data-testid="advisor-input"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="w-10 h-10 rounded-lg bg-degen-purple flex items-center justify-center hover:shadow-[0_0_20px_rgba(157,0,255,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="advisor-send-btn"
                >
                  <Send size={18} className="text-white" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MemeademicAdvisor;
